import { Link } from "@tanstack/react-router";
import { Lock, Sparkles } from "lucide-react";
import { useMembership } from "@/hooks/useMembership";
import {
  practiceLinkProps,
  practicesForArticle,
  type ArticlePractice,
} from "@/data/article-practice";

/**
 * Public article → member-only practices. The educational content above stays
 * free and indexable; the guided exercises themselves belong to membership.
 *
 * Reusable across every article: each article is assigned its own relevant
 * practices in src/data/article-practice.ts, while the design and the
 * membership logic stay identical everywhere.
 */

function PracticeCard({ practice, hasAccess }: { practice: ArticlePractice; hasAccess: boolean }) {
  const linkProps = practiceLinkProps(practice, hasAccess);

  return (
    <Link
      {...(linkProps as { to: string })}
      className="group flex h-full flex-col rounded-2xl border border-gold/30 bg-background/70 p-4 text-start transition hover:border-gold/60 hover:bg-gold-light/10 sm:p-5"
    >

      <span className="inline-flex items-center gap-1.5 self-start rounded-full bg-gold-light/40 px-2.5 py-1 text-[11px] font-semibold text-[var(--gold-deep)]">
        {hasAccess ? <Sparkles className="h-3 w-3" aria-hidden /> : <Lock className="h-3 w-3" aria-hidden />}
        {hasAccess ? "Included with your membership" : "Members only"}
      </span>
      <span className="mt-3 block text-base font-semibold text-foreground">{practice.title}</span>
      <span className="mt-1.5 block text-sm leading-relaxed text-foreground/70">{practice.blurb}</span>
      <span className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-[var(--gold-deep)] group-hover:underline">
        Try this exercise
        <span aria-hidden className="inline-block rtl:rotate-180">
          →
        </span>
      </span>
    </Link>
  );
}

export function ArticlePracticeCTA({ slug, category }: { slug: string; category?: string }) {
  const practices = practicesForArticle(slug, category);
  const { isMember, isAdmin, isAuthed, authResolved } = useMembership();
  const hasAccess = isMember || isAdmin;

  if (practices.length === 0) return null;

  return (
    <section
      aria-labelledby="article-practices-heading"
      className="mt-10 rounded-3xl border border-gold/35 bg-gold-light/15 p-5 sm:p-7"
    >
      <h2 id="article-practices-heading" className="text-lg font-semibold text-foreground sm:text-xl">
        Practices that go with this article
      </h2>
      <p className="mt-1.5 text-sm leading-relaxed text-foreground/70">
        {hasAccess
          ? "Two guided practices chosen to match what you have just been reading. Use whichever feels right — there is no order to follow."
          : "These guided practices are part of Recovery Trauma™ membership. They were chosen to match what you have just been reading."}
      </p>

      <div className="mt-5 grid gap-3 sm:grid-cols-2">
        {practices.map((practice) => (
          <PracticeCard key={practice.title} practice={practice} hasAccess={hasAccess} />
        ))}
      </div>


      {!hasAccess && (
        <div className="mt-6 rounded-2xl border border-gold/30 bg-background/60 p-5 text-center sm:p-6">
          <p className="text-base font-semibold text-foreground">
            Ready to put what you've learned into practice?
          </p>
          <p className="mx-auto mt-2 max-w-xl text-sm leading-relaxed text-foreground/70">
            Recovery Trauma™ gives you access to guided exercises, grounding and nervous-system
            practices, journals, healing journeys, assessments and other recovery resources — all in
            one place.
          </p>
          <Link
            to="/choose-membership"
            className="mt-4 inline-flex min-h-12 items-center justify-center gap-1.5 rounded-full bg-gradient-to-b from-gold to-[var(--gold-deep)] px-6 text-sm font-semibold text-background shadow-sm transition hover:brightness-105"
          >
            Join Recovery Trauma™
            <span aria-hidden className="inline-block rtl:rotate-180">
              →
            </span>
          </Link>
          <p className="mt-3 text-xs text-foreground/60">
            {/* Signed-in visitors don't need a sign-in nudge — they already
                have an account and are one step away from membership. */}
            {!authResolved || !isAuthed ? (
              <>
                Already a member?{" "}
                <Link to="/login" className="font-medium underline underline-offset-2">
                  Sign in
                </Link>{" "}
                to go straight there.
              </>
            ) : (
              "You're signed in — choose a membership to unlock these practices."
            )}
          </p>
        </div>
      )}
    </section>
  );
}

export default ArticlePracticeCTA;

/**
 * Compact single-practice card for placing naturally part-way through an
 * article. Uses the same assignment and membership logic as the section above.
 */
export function InlineArticlePractice({ slug, category }: { slug: string; category?: string }) {
  const practices = practicesForArticle(slug, category);
  const { isMember, isAdmin } = useMembership();
  const hasAccess = isMember || isAdmin;
  const practice = practices[0];
  if (!practice) return null;

  return (
    <aside className="my-10 rounded-2xl border border-gold/30 bg-gold-light/15 p-4 sm:p-5">
      <p className="inline-flex items-center gap-1.5 rounded-full bg-gold-light/40 px-2.5 py-1 text-[11px] font-semibold text-[var(--gold-deep)]">
        {hasAccess ? <Sparkles className="h-3 w-3" aria-hidden /> : <Lock className="h-3 w-3" aria-hidden />}
        {hasAccess ? "Included with your membership" : "Members only"}
      </p>
      <p className="mt-2.5 text-base font-semibold text-foreground">{practice.title}</p>
      <p className="mt-1 text-sm leading-relaxed text-foreground/70">{practice.blurb}</p>
      <Link
        {...(practiceLinkProps(practice, hasAccess) as { to: string })}
        className="mt-3 inline-flex min-h-11 items-center gap-1.5 text-sm font-semibold text-[var(--gold-deep)] hover:underline"
      >

        Try this exercise
        <span aria-hidden className="inline-block rtl:rotate-180">
          →
        </span>
      </Link>
    </aside>
  );
}
